import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '@trovata/app/core/models/config.model';

/** Report export file types the server's {@code handleReportExport} accepts. */
export type ReportExportType = 'XLSX' | 'PDF' | 'SQL';

/**
 * Report Apps — download client for {@code ?action=reportExport}. Models the
 * repo's established blob-anchor idiom ({@code TsmMatrixApiService.exportExcel}
 * + {@code TsmMatrixModalHostComponent.onExportExcel}): GET the bytes/text,
 * then {@code URL.createObjectURL} → transient {@code <a download>} →
 * {@code revokeObjectURL}. No {@code file-saver} dependency (not the repo
 * convention).
 *
 * <p>The server streams from the SAME cached report processor that ran
 * {@code runReport} (by formId), so a Search must have run first. Auth is
 * handled by {@link AuthInterceptor} via the {@code atomApiUrl} allowlist
 * (Jackson Rule #2) — no manual headers.
 *
 * <p>{@code XLSX}/{@code PDF} are binary ({@code responseType: 'blob'} → file
 * download); {@code SQL} is text ({@code responseType: 'text'} → returned to
 * the caller to show in a {@code MatDialog}).
 */
@Injectable({ providedIn: 'root' })
export class ReportDownloadService {
	private readonly baseUrl: string;

	constructor(
		private readonly http: HttpClient,
		@Inject(ENVIRONMENT_TOKEN) private readonly environment: DefaultEnvironment,
	) {
		this.baseUrl = `${this.environment.atomApiUrl}/api/v1/app-data`;
	}

	/**
	 * Fetch the XLSX/PDF artifact as a Blob. Caller passes it to
	 * {@link #triggerDownload}. The server keys the export off {@code formId}
	 * (same cached processor that ran the report).
	 */
	fetchFile(appName: string, formId: string, type: 'XLSX' | 'PDF'): Observable<Blob> {
		const params: HttpParams = new HttpParams()
			.set('action', 'reportExport')
			.set('app', appName)
			.set('type', type)
			.set('formId', formId);
		return this.http.get(this.baseUrl, { params, responseType: 'blob' });
	}

	/**
	 * Fetch the report SQL as text (admin-only server-side via {@code fscUser()};
	 * the SQL toolbar button is also entitlement-gated by the toolbar projection).
	 */
	fetchSql(appName: string, formId: string): Observable<string> {
		const params: HttpParams = new HttpParams()
			.set('action', 'reportExport')
			.set('app', appName)
			.set('type', 'SQL')
			.set('formId', formId);
		return this.http.get(this.baseUrl, { params, responseType: 'text' });
	}

	/**
	 * Blob-anchor download — the repo idiom (see {@code TsmMatrixModalHostComponent}).
	 * Builds a transient object URL, clicks a synthetic {@code <a download>},
	 * then revokes. {@code filename} should be {@code <app>_<referenceId>.<ext>}.
	 */
	/** Export filename: the app plus the run's reference id, so two runs of the same report do
	 *  not overwrite each other in the browser's download folder. SSOT — the toolbar exports and
	 *  the format=excel run-and-export both name files through here. */
	exportFilename(appName: string, referenceId: string | null | undefined, ext: string): string {
		const ref: string = referenceId ? `_${referenceId}` : '';
		return `${appName}${ref}.${ext}`;
	}

	triggerDownload(blob: Blob, filename: string): void {
		const url: string = URL.createObjectURL(blob);
		const anchor: HTMLAnchorElement = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	}
}
